<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';

interface Props {
    id: string;
    label: string;
    modelValue?: string;
    disabled?: boolean;
    error?: string;
    required?: boolean;
    capitalize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    disabled: false,
    error: undefined,
    required: false,
    capitalize: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();

function capitalizeWords(value: string): string {
    return value.replace(/(^|[\s'-])([a-z])/g, (match) => match.toUpperCase());
}

function handleUpdate(value: string) {
    emit('update:modelValue', props.capitalize ? capitalizeWords(value) : value);
}
</script>

<template>
    <div :class="['form-field', { 'form-field--invalid': !!error }]">
        <FloatLabel variant="on" class="form-field__float">
            <slot>
                <InputText
                    :id="id"
                    class="form-field__input"
                    :model-value="modelValue"
                    :disabled="disabled"
                    :autocapitalize="capitalize ? 'words' : undefined"
                    placeholder=" "
                    @update:model-value="handleUpdate($event as string)"
                />
            </slot>
            <label :for="id" :data-required="required ? 'true' : 'false'">{{ label }}</label>
        </FloatLabel>
        <span v-if="error" class="form-field__error">{{ error }}</span>
    </div>
</template>

<style lang="scss" scoped>
.form-field {
    width: 100%;

    // Wrapper components need full width
    :deep(.p-inputnumber) {
        width: 100% !important;
    }

    // All <input> elements (InputText, InputMask, inner input of InputNumber)
    :deep(input) {
        width: 100% !important;
        height: 57px !important;
        border-radius: 10px !important;
        border: 1px solid $border-gray-color !important;
        box-shadow: none !important;
        font-family: $font-family-archivo !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        color: $black-color !important;
        background: $white-color !important;
        padding: 20px 25px !important;

        &:focus {
            border-color: $main-orange !important;
            box-shadow: none !important;
        }

        &:disabled {
            background: #F4F4F4 !important;
            opacity: 0.6;
        }
    }

    // Select component
    :deep(.p-select) {
        width: 100% !important;
        height: 57px !important;
        border-radius: 10px !important;
        border-color: $border-gray-color !important;
        box-shadow: none !important;
        font-family: $font-family-archivo !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        color: $black-color !important;
        background: $white-color !important;
        

        .p-select-label {
            padding: 20px 25px !important;
            display: flex;
            align-items: center;
        }

        .p-select-dropdown {
            padding-right: 26px;

            .p-icon {
                width: 14px;
                height: 14px;
                color: $black-color;
            }
        }
    }

    // Textarea
    :deep(textarea) {
        width: 100% !important;
        border-radius: 10px !important;
        border: 1px solid $border-gray-color !important;
        box-shadow: none !important;
        font-family: $font-family-archivo !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        color: $black-color !important;
        background: $white-color !important;
        padding: 20px 25px !important;
        resize: vertical;

        &:focus {
            border-color: $main-orange !important;
            box-shadow: none !important;
        }
    }

    // Label
    :deep(label) {
        left: 25px !important;
        font-family: $font-family-archivo !important;
        font-weight: 300 !important;
        font-size: 14px !important;
        line-height: 15px !important;
        color: rgba($black-color, 0.6) !important;

        &[data-required="true"]::after {
            content: "*";
            color: $error-red;
            font-weight: bold;
            margin-left: 4px;
            transition: transform 0.2s ease;
        }
    }

    // Float label wrapper
    &__float {
        width: 100%;
    }

    // Invalid state — red border on all input types
    &--invalid {
        :deep(input) {
            border-color: $error-red !important;
        }

        :deep(.p-select) {
            border-color: $error-red !important;
        }

        :deep(textarea) {
            border-color: $error-red !important;
        }
    }

    // Inline error message
    &__error {
        display: block;
        margin-top: 5px;
        padding-left: 25px;
        font-family: $font-family-archivo;
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: $error-red;
    }
}
</style>
